import { FC, FormEvent, useRef, useState } from "react";
import { useFetcher, useNavigate, useParams } from "react-router";
import {
  Alert,
  Box,
  Button,
  ButtonList,
  Input,
  Text,
} from "@postenbring/hedwig-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons/faCircleCheck";
import { useRootPath } from "../infrastructure/translation/useRootPath";
import Translated from "../infrastructure/translation/Translated";
import { CenteredSpinner } from "../CenteredSpinner/CenteredSpinner";
import styles from "./NewCertificatePage.module.css";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import { CreateCertificateResult } from "../Certificate/certificatesRouteContext";
import { formatTimestamp } from "../Certificate/formatTimestamp";
import { DetailList, DetailRow } from "../DetailList/DetailList";
import { useSelectedOrganization } from "../infrastructure/organization/OrganizationContext";
import { linkExists } from "../infrastructure/entrypoint/link";
import { CertificateFormHandle } from "./certificateForm";
import { GenerateCertificate } from "./GenerateCertificate";
import { UploadCertificate } from "./UploadCertificate";

export const NewCertificatePage: FC = () => {
  const { orgnr } = useParams<{ orgnr: string }>();
  const rootPath = useRootPath();
  const navigate = useNavigate();
  const translate = useTranslate();
  const fetcher = useFetcher<CreateCertificateResult>();
  const organizationPath = `${rootPath}/organization/${orgnr}`;
  const canUpload = linkExists(
    "upload_certificate",
    useSelectedOrganization()._links,
  );

  const canGenerate = linkExists(
    "create_certificate",
    useSelectedOrganization()._links,
  );

  const canChooseMode = canUpload && canGenerate;

  const isUploadModeDefault = !canGenerate && canUpload;

  const [uploadMode, setUploadMode] = useState(isUploadModeDefault);
  const [displayName, setDisplayName] = useState("");
  const [errorDismissed, setErrorDismissed] = useState(false);
  const formRef = useRef<CertificateFormHandle>(null);

  const submitting = fetcher.state !== "idle";
  const createdCertificate =
    fetcher.data && "created" in fetcher.data ? fetcher.data.created : null;
  const uploadedCertificate =
    fetcher.data && "uploaded" in fetcher.data ? fetcher.data.uploaded : null;
  const error =
    !errorDismissed && fetcher.data && "error" in fetcher.data
      ? fetcher.data.error
      : null;

  const clearError = () => setErrorDismissed(true);

  const selectMode = (upload: boolean) => {
    setUploadMode(upload);
    setErrorDismissed(true);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErrorDismissed(false);
    formRef.current?.submit();
  };

  const modeCardClass = (active: boolean) =>
    active ? `${styles.modeCard} ${styles.modeCardActive}` : styles.modeCard;

  const handleDownload = () => {
    if (!createdCertificate) return;

    const binary = atob(createdCertificate.keystoreP12);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: "application/x-pkcs12" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${createdCertificate.certificateRepresentation.certificate.serialNumber}.p12`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (submitting) {
    return (
      <div className={styles.container}>
        <CenteredSpinner />
      </div>
    );
  }

  const certificate =
    createdCertificate?.certificateRepresentation.certificate ??
    uploadedCertificate?.certificate;

  if (certificate) {
    return (
      <div className={styles.container}>
        <div className={styles.createdHeader}>
          <FontAwesomeIcon
            icon={faCircleCheck}
            className={styles.createdIcon}
            aria-hidden
          />
          <Text variant="h1" as="h1" size="min">
            <Translated
              textKey={
                createdCertificate
                  ? "new_certificate.created_header"
                  : "new_certificate.upload_created_header"
              }
            />
          </Text>
        </div>

        {createdCertificate && (
          <Alert data-color="warning" role="alert" className={styles.warning}>
            <Alert.Description>
              <Translated textKey={"new_certificate.created_warning"} />
            </Alert.Description>
          </Alert>
        )}

        <Box variant="white" className={styles.card}>
          <DetailList>
            <DetailRow label={<Translated textKey={"certificate.name"} />}>
              {certificate.displayName}
            </DetailRow>
            <DetailRow
              label={<Translated textKey={"certificate.serial_number"} />}
            >
              <span className={styles.breakAll}>
                {certificate.serialNumber}
              </span>
            </DetailRow>
            <DetailRow label={<Translated textKey={"certificate.dn"} />}>
              <span className={styles.breakAll}>{certificate.subjectDn}</span>
            </DetailRow>
            <DetailRow
              label={<Translated textKey={"certificate.created_time"} />}
            >
              {formatTimestamp(certificate.createdTime)}
            </DetailRow>
          </DetailList>

          <ButtonList className={styles.actions}>
            {createdCertificate && (
              <Button size="small" onClick={handleDownload}>
                <Translated textKey={"new_certificate.download_keystore"} />
              </Button>
            )}
            <Button
              size="small"
              variant="secondary"
              onClick={() => navigate(organizationPath)}
            >
              <Translated textKey={"generic.to_overview"} />
            </Button>
          </ButtonList>
        </Box>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Text variant="h1" as="h1" size="min" className={styles.title}>
        <Translated
          textKey={
            uploadMode
              ? "new_certificate.upload_header"
              : "new_certificate.header"
          }
        />
      </Text>
      <Text variant="body-small" className={styles.intro}>
        <Translated
          textKey={
            uploadMode
              ? "new_certificate.upload_description"
              : "new_certificate.description"
          }
        />
      </Text>

      <Box variant="white" asChild>
        <form onSubmit={handleSubmit} className={styles.card}>
          {canChooseMode && (
            <fieldset className={styles.modeChoice}>
              <legend className={styles.modeLegend}>
                <Translated textKey={"new_certificate.mode_legend"} />
              </legend>
              <button
                type="button"
                className={modeCardClass(!uploadMode)}
                aria-pressed={!uploadMode}
                onClick={() => selectMode(false)}
              >
                <span className={styles.modeTitle}>
                  <Translated textKey={"new_certificate.mode_generate"} />
                </span>
                <span className={styles.modeHelp}>
                  <Translated textKey={"new_certificate.mode_generate_help"} />
                </span>
              </button>
              <button
                type="button"
                className={modeCardClass(uploadMode)}
                aria-pressed={uploadMode}
                onClick={() => selectMode(true)}
              >
                <span className={styles.modeTitle}>
                  <Translated textKey={"new_certificate.mode_upload"} />
                </span>
                <span className={styles.modeHelp}>
                  <Translated textKey={"new_certificate.mode_upload_help"} />
                </span>
              </button>
            </fieldset>
          )}

          <div className={styles.field}>
            <Input
              label={translate("new_certificate.name")}
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setErrorDismissed(true);
              }}
              required
            />
            <Text variant="body-small" className={styles.help}>
              <Translated textKey={"new_certificate.name_help"} />
            </Text>
          </div>

          {uploadMode ? (
            <UploadCertificate
              ref={formRef}
              displayName={displayName}
              fetcher={fetcher}
              clearError={clearError}
            />
          ) : (
            <GenerateCertificate
              ref={formRef}
              displayName={displayName}
              fetcher={fetcher}
              clearError={clearError}
            />
          )}

          {error && <p className={styles.error}>{error}</p>}

          <ButtonList className={styles.actions}>
            <Button size="small" type="submit">
              <Translated
                textKey={
                  uploadMode
                    ? "new_certificate.upload_submit"
                    : "new_certificate.submit"
                }
              />
            </Button>
            <Button
              size="small"
              variant="secondary"
              type="button"
              onClick={() => navigate(`${organizationPath}/certificates`)}
            >
              <Translated textKey={"generic.cancel"} />
            </Button>
          </ButtonList>
        </form>
      </Box>
    </div>
  );
};
